import {
  AlertTriangle,
  Camera,
  Car,
  CheckCircle,
  Clock,
  Mail,
  Phone,
  Shield,
} from "lucide-react";
import { useEffect, useState } from "react";

import type { EmergencyContact } from "@/types/landing";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { submitVehicleReport } from "@/services/apiService";
import { EmergencyContactsService } from "@/services/landing/emergencyContactsService";

export default function ReportVehicle() {
  const [emergencyContacts, setEmergencyContacts] = useState<
    EmergencyContact[]
  >([]);
  const [contactsLoading, setContactsLoading] = useState(true);

  const [formData, setFormData] = useState({
    attachments: undefined as File[] | undefined,
    dateTime: "",
    description: "",
    driverName: "",
    issueType: "",
    licensePlate: "",
    location: "",
    priority: "",
    reporterEmail: "",
    reporterName: "",
    reporterPhone: "",
    tripId: "",
    vehicleType: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [apiError, setApiError] = useState<null | string>(null);

  useEffect(() => {
    const fetchEmergencyContacts = async () => {
      try {
        setContactsLoading(true);
        const data = await EmergencyContactsService.getAll();
        const activeContacts = data
          .filter((contact) => contact.isActive)
          .sort((a, b) => a.order - b.order);
        setEmergencyContacts(activeContacts);
      } catch (error) {
        console.error("Failed to load emergency contacts:", error);
        setEmergencyContacts([]);
      } finally {
        setContactsLoading(false);
      }
    };

    fetchEmergencyContacts();
  }, []);

  // Validation function
  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.vehicleType)
      newErrors.vehicleType = "Vehicle type is required.";
    if (!formData.licensePlate)
      newErrors.licensePlate = "License plate is required.";
    if (!formData.issueType) newErrors.issueType = "Issue type is required.";
    if (!formData.priority) newErrors.priority = "Priority is required.";
    if (!formData.description)
      newErrors.description = "Description is required.";
    if (!formData.location) newErrors.location = "Location is required.";
    if (!formData.dateTime) newErrors.dateTime = "Date & Time is required.";
    if (!formData.reporterName)
      newErrors.reporterName = "Your name is required.";
    if (!formData.reporterEmail) {
      newErrors.reporterEmail = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.reporterEmail)) {
      newErrors.reporterEmail = "Email is invalid.";
    }
    if (!formData.reporterPhone)
      newErrors.reporterPhone = "Phone number is required.";
    return newErrors;
  };

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "application/pdf",
    "video/mp4",
    "video/quicktime", // mov
  ];

const maxFileSize = 4.5 * 1024 * 1024; // 4.5MB

const handleInputChange = (
  e: React.ChangeEvent<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >,
) => {
  const { name, type, value } = e.target;

  if (type === "file") {
    const fileInput = e.target as HTMLInputElement;
    const files = fileInput.files;

    let fileError = "";
    let fileArray: File[] | undefined = undefined;

    if (files && files.length > 0) {
      fileArray = Array.from(files);

      for (const file of fileArray) {
        if (!allowedTypes.includes(file.type)) {
          fileError = "Only JPG, PNG, PDF, MP4, MOV files are allowed.";
          break;
        }

        if (file.size > maxFileSize) {
          fileError = `"${file.name}" is larger than 4.5MB.`;
          break;
        }
      }
    }

    if (fileError) {
      // Clear the actual file input
      fileInput.value = "";

      setErrors((prev) => ({
        ...prev,
        [name]: fileError,
      }));

      setFormData((prev) => ({
        ...prev,
        [name]: undefined,
      }));

      return;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setFormData((prev) => ({
      ...prev,
      [name]: fileArray,
    }));
  } else {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    setIsSubmitting(true);

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }
    setErrors({});

    try {
      const result = await submitVehicleReport(formData);
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setApiError(result.message || "Failed to submit report.");
      }
    } catch (error) {
      console.log("API error:", error);
      // Try to extract message from error or error.response or error.data
      let message = "Failed to submit report.";
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        (error as { response?: { data?: { message?: string } } }).response?.data
          ?.message
      ) {
        message = (error as { response: { data: { message: string } } })
          .response.data.message;
      } else if (error && typeof error === "object" && "message" in error) {
        message = (error as { message: string }).message;
      } else if (typeof error === "string") {
        message = error;
      }
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const vehicleTypes = [
    { label: "Car", value: "car" },
    { label: "Scooter", value: "scooter" },
    { label: "Motorbike", value: "motorbike" },
    { label: "Bicycle", value: "bicycle" },
    { label: "Van/Bakkie", value: "van" },
    { label: "Other", value: "other" },
  ];

  const issueTypes = [
    { label: "Reckless Driving", severity: "high", value: "reckless-driving" },
    {
      label: "Poor Vehicle Condition",
      severity: "medium",
      value: "vehicle-condition",
    },
    {
      label: "Unprofessional Behavior",
      severity: "medium",
      value: "unprofessional-behavior",
    },
    { label: "Safety Violation", severity: "high", value: "safety-violation" },
    { label: "Illegal Parking", severity: "low", value: "illegal-parking" },
    { label: "Harassment", severity: "high", value: "harassment" },
    { label: "Overcharging", severity: "medium", value: "overcharging" },
    { label: "Vehicle Damage", severity: "low", value: "vehicle-damage" },
    { label: "Other", severity: "medium", value: "other" },
  ];

  const priorityLevels = [
    {
      description: "Minor issues, non-urgent",
      label: "Low Priority",
      value: "low",
    },
    {
      description: "Moderate concern, needs attention",
      label: "Medium Priority",
      value: "medium",
    },
    {
      description: "Serious issue, urgent attention required",
      label: "High Priority",
      value: "high",
    },
    {
      description: "Immediate safety concern",
      label: "Emergency",
      value: "emergency",
    },
  ];

  if (isSubmitted) {
    return (
      <>
        <AppNavbar />
        <div className="min-h-screen bg-base-200 pt-16">
          <Breadcrumbs />
          <div className="flex items-center justify-center px-4 py-16">
            <div className="max-w-lg text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success/20">
                <CheckCircle className="text-success" size={40} />
              </div>
              <h1 className="mb-4 text-3xl font-bold text-success">
                Vehicle Report Submitted!
              </h1>
              <p className="mb-6 text-base-content/70">
                Thank you for reporting this vehicle. We take all reports
                seriously and will investigate immediately.
              </p>
              <div className="space-y-3">
                <div className="alert alert-info">
                  <Shield size={20} />
                  <div>
                    <h4 className="font-semibold">Report ID</h4>
                    <p className="text-sm">
                      VR{Math.floor(Math.random() * 100000)}
                    </p>
                  </div>
                </div>
                <div className="alert alert-warning">
                  <Clock size={20} />
                  <div>
                    <h4 className="font-semibold">Investigation Timeline</h4>
                    <p className="text-sm">
                      We will review within 2-4 hours for urgent cases
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 space-y-2">
                <button
                  className="btn btn-primary w-full"
                  onClick={() => window.location.reload()}
                >
                  Report Another Vehicle
                </button>
                <button
                  className="btn btn-ghost w-full"
                  onClick={() => window.history.back()}
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AppNavbar />
      <div className="min-h-screen bg-base-200 pt-16">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="bg-base-200 py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-300 sm:mb-6 sm:h-20 sm:w-20 lg:h-24 lg:w-24">
                <Car className="h-8 w-8 text-error sm:h-10 sm:w-10 lg:h-12 lg:w-12" />
              </div>
              <h1 className="mb-3 text-3xl font-bold sm:mb-4 sm:text-4xl md:text-5xl lg:text-6xl">
                Report a Vehicle
              </h1>
              <p className="mx-auto max-w-2xl text-base opacity-90 sm:text-lg lg:text-xl">
                Help us maintain safety standards by reporting vehicles or
                drivers that do not meet our guidelines.
              </p>
            </div>
          </div>
        </section>

        {/* Report Form */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-4xl px-4">
            <div className="card bg-base-100 shadow-2xl">
              <div className="card-body p-4 sm:p-6 lg:p-8">
                <h2 className="card-title mb-4 text-2xl sm:mb-6 sm:text-3xl">
                  Vehicle Report Form
                </h2>

                {apiError && (
                  <div className="alert alert-error mb-4">{apiError}</div>
                )}

                <form
                  className="space-y-4 sm:space-y-6"
                  onSubmit={handleSubmit}
                >
                  {/* Vehicle Information */}
                  <div className="rounded-lg bg-base-200 p-4 sm:p-6">
                    <h3 className="mb-3 text-lg font-semibold text-primary sm:mb-4 sm:text-xl">
                      Vehicle Information
                    </h3>

                    <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Vehicle Type
                          </span>
                        </label>
                        <select
                          className="select select-bordered select-sm w-full sm:select-md"
                          name="vehicleType"
                          onChange={handleInputChange}
                          required
                          value={formData.vehicleType}
                        >
                          <option value="">Select vehicle type</option>
                          {vehicleTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        {errors.vehicleType && (
                          <span className="text-sm text-error">
                            {errors.vehicleType}
                          </span>
                        )}
                      </div>

                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            License Plate Number
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="licensePlate"
                          onChange={handleInputChange}
                          placeholder="e.g., CA 123-456"
                          required
                          type="text"
                          value={formData.licensePlate}
                        />
                        {errors.licensePlate && (
                          <span className="text-sm text-error">
                            {errors.licensePlate}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="form-control mt-3 sm:mt-4">
                      <label className="label pb-1">
                        <span className="label-text text-sm font-medium sm:text-base">
                          Driver Name (if known)
                        </span>
                      </label>
                      <input
                        className="input input-sm input-bordered w-full sm:input-md"
                        name="driverName"
                        onChange={handleInputChange}
                        placeholder="Driver's name or nickname"
                        type="text"
                        value={formData.driverName}
                      />
                    </div>
                  </div>

                  {/* Issue Details */}
                  <div className="rounded-lg bg-base-200 p-4 sm:p-6">
                    <h3 className="mb-3 text-lg font-semibold text-primary sm:mb-4 sm:text-xl">
                      Issue Details
                    </h3>

                    <div className="form-control mb-3 sm:mb-4">
                      <label className="label pb-1">
                        <span className="label-text text-sm font-medium sm:text-base">
                          Type of Issue
                        </span>
                      </label>
                      <select
                        className="select select-bordered select-sm w-full sm:select-md"
                        name="issueType"
                        onChange={handleInputChange}
                        required
                        value={formData.issueType}
                      >
                        <option value="">Select issue type</option>
                        {issueTypes.map((issue) => (
                          <option key={issue.value} value={issue.value}>
                            {issue.label}
                          </option>
                        ))}
                      </select>
                      {errors.issueType && (
                        <span className="text-sm text-error">
                          {errors.issueType}
                        </span>
                      )}
                    </div>

                    <div className="form-control mb-3 sm:mb-4">
                      <label className="label pb-1">
                        <span className="label-text text-sm font-medium sm:text-base">
                          Priority Level
                        </span>
                      </label>
                      <div className="grid gap-2 sm:grid-cols-2 sm:gap-3">
                        {priorityLevels.map((priority) => (
                          <label
                            className="label cursor-pointer justify-start gap-2 rounded-lg border p-3 hover:bg-base-300 sm:gap-3 sm:p-4"
                            key={priority.value}
                          >
                            <input
                              className={`radio radio-sm sm:radio-md ${
                                priority.value === "emergency"
                                  ? "radio-error"
                                  : priority.value === "high"
                                    ? "radio-warning"
                                    : priority.value === "medium"
                                      ? "radio-info"
                                      : "radio-success"
                              }`}
                              name="priority"
                              onChange={handleInputChange}
                              required
                              type="radio"
                              value={priority.value}
                            />
                            <div>
                              <div className="text-sm font-medium sm:text-base">
                                {priority.label}
                              </div>
                              <div className="text-xs text-base-content/70 sm:text-sm">
                                {priority.description}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                      {errors.priority && (
                        <span className="text-sm text-error">
                          {errors.priority}
                        </span>
                      )}
                    </div>

                    <div className="form-control">
                      <label className="label pb-1">
                        <span className="label-text text-sm font-medium sm:text-base">
                          Detailed Description
                        </span>
                      </label>
                      <textarea
                        className="textarea textarea-bordered textarea-sm h-24 w-full sm:textarea-md sm:h-32"
                        name="description"
                        onChange={handleInputChange}
                        placeholder="Please provide a detailed description of what happened, including any relevant circumstances..."
                        required
                        value={formData.description}
                      ></textarea>
                      {errors.description && (
                        <span className="text-sm text-error">
                          {errors.description}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Location and Time */}
                  <div className="rounded-lg bg-base-200 p-4 sm:p-6">
                    <h3 className="mb-3 text-lg font-semibold text-primary sm:mb-4 sm:text-xl">
                      Location & Time
                    </h3>

                    <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Location
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="location"
                          onChange={handleInputChange}
                          placeholder="Street address or landmark"
                          required
                          type="text"
                          value={formData.location}
                        />
                        {errors.location && (
                          <span className="text-sm text-error">
                            {errors.location}
                          </span>
                        )}
                      </div>

                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Date & Time
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="dateTime"
                          onChange={handleInputChange}
                          required
                          type="datetime-local"
                          value={formData.dateTime}
                        />
                        {errors.dateTime && (
                          <span className="text-sm text-error">
                            {errors.dateTime}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="form-control mt-3 sm:mt-4">
                      <label className="label pb-1">
                        <span className="label-text text-sm font-medium sm:text-base">
                          Trip ID (if applicable)
                        </span>
                      </label>
                      <input
                        className="input input-sm input-bordered w-full sm:input-md"
                        name="tripId"
                        onChange={handleInputChange}
                        placeholder="e.g., HR123456789"
                        type="text"
                        value={formData.tripId}
                      />
                    </div>
                  </div>

                  {/* Reporter Information */}
                  <div className="rounded-lg bg-base-200 p-4 sm:p-6">
                    <h3 className="mb-3 text-lg font-semibold text-primary sm:mb-4 sm:text-xl">
                      Your Information
                    </h3>

                    <div className="grid gap-3 sm:gap-4 md:grid-cols-3">
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Your Name
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="reporterName"
                          onChange={handleInputChange}
                          placeholder="Full name"
                          required
                          type="text"
                          value={formData.reporterName}
                        />
                        {errors.reporterName && (
                          <span className="text-sm text-error">
                            {errors.reporterName}
                          </span>
                        )}
                      </div>

                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Email Address
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="reporterEmail"
                          onChange={handleInputChange}
                          placeholder="your.email@example.com"
                          required
                          type="email"
                          value={formData.reporterEmail}
                        />
                        {errors.reporterEmail && (
                          <span className="text-sm text-error">
                            {errors.reporterEmail}
                          </span>
                        )}
                      </div>

                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Phone Number
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="reporterPhone"
                          onChange={handleInputChange}
                          placeholder="+27 XX XXX XXXX"
                          type="tel"
                          value={formData.reporterPhone}
                        />
                        {errors.reporterPhone && (
                          <span className="text-sm text-error">
                            {errors.reporterPhone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* File Attachments */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-sm font-medium sm:text-base">
                        Evidence (Photos/Videos)
                      </span>
                    </label>
                    <input
                      accept=".jpg,.jpeg,.png,.mp4,.mov,.pdf,image/*,video/*"
                      className="file-input file-input-bordered file-input-sm w-full sm:file-input-md"
                      multiple
                      name="attachments"
                      onChange={handleInputChange}
                      type="file"
                    />
                    <div className="label">
                      <span className="label-text-alt text-xs sm:text-sm">
                        <Camera className="mr-1 inline h-3 w-3 sm:h-4 sm:w-4" />
                        Upload photos or videos as evidence (Max 4.5MB each)
                      </span>
                    </div>
                    {errors.attachments && (
                      <span className="text-sm text-error">
                        {errors.attachments}
                      </span>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="form-control pt-3 sm:pt-4">
                    <button
                      className="btn btn-error btn-sm w-full sm:btn-md lg:btn-lg"
                      disabled={isSubmitting}
                      type="submit"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="loading loading-spinner loading-sm"></span>
                          <span className="hidden sm:inline">
                            Submitting Report...
                          </span>
                          <span className="sm:hidden">Submitting...</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="hidden sm:inline">
                            Submit Vehicle Report
                          </span>
                          <span className="sm:hidden">Submit Report</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>

                <div className="mt-4 rounded-lg bg-info/10 p-3 sm:mt-6 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <Shield className="mt-1 h-4 w-4 text-info sm:h-5 sm:w-5" />
                    <div>
                      <h4 className="text-sm font-semibold text-info sm:text-base">
                        Anonymous Reporting
                      </h4>
                      <p className="text-xs text-base-content/70 sm:text-sm">
                        Your report will be treated confidentially. We may
                        contact you for additional information if needed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What Happens Next */}
        <section className="bg-base-100 py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <h2 className="mb-8 text-center text-2xl font-bold sm:mb-12 sm:text-3xl">
              What Happens Next?
            </h2>
            <div className="grid gap-6 sm:gap-8 md:grid-cols-4">
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-content sm:mb-4 sm:h-14 sm:w-14 lg:h-16 lg:w-16 lg:text-xl">
                  1
                </div>
                <h3 className="mb-2 text-base font-semibold sm:text-lg">
                  Report Review
                </h3>
                <p className="text-xs text-base-content/70 sm:text-sm">
                  Our safety team reviews your report within 2-4 hours
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-lg font-bold text-secondary-content sm:mb-4 sm:h-14 sm:w-14 lg:h-16 lg:w-16 lg:text-xl">
                  2
                </div>
                <h3 className="mb-2 text-base font-semibold sm:text-lg">
                  Investigation
                </h3>
                <p className="text-xs text-base-content/70 sm:text-sm">
                  We investigate the matter and contact relevant parties
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-lg font-bold text-accent-content sm:mb-4 sm:h-14 sm:w-14 lg:h-16 lg:w-16 lg:text-xl">
                  3
                </div>
                <h3 className="mb-2 text-base font-semibold sm:text-lg">
                  Action Taken
                </h3>
                <p className="text-xs text-base-content/70 sm:text-sm">
                  Appropriate action is taken based on our findings
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-success text-lg font-bold text-success-content sm:mb-4 sm:h-14 sm:w-14 lg:h-16 lg:w-16 lg:text-xl">
                  4
                </div>
                <h3 className="mb-2 text-base font-semibold sm:text-lg">
                  Follow-up
                </h3>
                <p className="text-xs text-base-content/70 sm:text-sm">
                  We will update you on the outcome if contact info is provided
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center">
              <h2 className="mb-6 text-2xl font-bold sm:mb-8 sm:text-3xl">
                Need Immediate Help?
              </h2>
              {contactsLoading ? (
                <div className="text-center">
                  <span className="loading loading-spinner loading-lg"></span>
                  <p className="mt-4">Loading contact information...</p>
                </div>
              ) : emergencyContacts.length > 0 ? (
                <div className="grid gap-4 sm:gap-6 md:grid-cols-3">
                  {emergencyContacts.slice(0, 3).map((contact) => (
                    <div
                      className="card bg-base-100 shadow-lg"
                      key={contact.id}
                    >
                      <div className="card-body p-4 text-center sm:p-6">
                        <Phone
                          className={`mx-auto mb-3 h-6 w-6 sm:mb-4 sm:h-8 sm:w-8 ${contact.iconColor || "text-primary"}`}
                        />
                        <h3
                          className={`card-title justify-center text-base sm:text-lg ${contact.iconColor || "text-primary"}`}
                        >
                          {contact.title}
                        </h3>
                        <p className="mb-3 text-sm text-base-content/70 sm:mb-4 sm:text-base">
                          {contact.phoneNumber}
                        </p>
                        <p className="text-xs text-base-content/60 sm:text-sm">
                          {contact.description}
                        </p>
                        <div className="mt-3 sm:mt-4">
                              {contact.phoneNumber.includes("@") ? (
                                <a
                                  className="btn btn-outline btn-xs sm:btn-sm"
                                  href={`mailto:${contact.phoneNumber}`}
                                >
                                  Email Now
                                </a>
                              ) : (
                                <a
                                  className="btn btn-outline btn-xs sm:btn-sm"
                                  href={`tel:${contact.phoneNumber.replace(/\D/g, "")}`}
                                >
                                  Call Now
                                </a>
                              )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid gap-4 sm:gap-6 md:grid-cols-3">
                  <div className="card bg-base-100 shadow-lg">
                    <div className="card-body p-4 text-center sm:p-6">
                      <Phone className="mx-auto mb-3 h-6 w-6 text-error sm:mb-4 sm:h-8 sm:w-8" />
                      <h3 className="card-title justify-center text-base text-error sm:text-lg">
                        Emergency
                      </h3>
                      <p className="mb-3 text-sm text-base-content/70 sm:mb-4 sm:text-base">
                        121 (Police)
                      </p>
                      <p className="text-xs text-base-content/60 sm:text-sm">
                        For immediate safety concerns
                      </p>
                    </div>
                  </div>

                  <div className="card bg-base-100 shadow-lg">
                    <div className="card-body p-4 text-center sm:p-6">
                      <Phone className="mx-auto mb-3 h-6 w-6 text-warning sm:mb-4 sm:h-8 sm:w-8" />
                      <h3 className="card-title justify-center text-base text-warning sm:text-lg">
                        Safety Hotline
                      </h3>
                      <p className="mb-3 text-sm text-base-content/70 sm:mb-4 sm:text-base">
                        0800-SAFETY
                      </p>
                      <p className="text-xs text-base-content/60 sm:text-sm">
                        24/7 safety support
                      </p>
                    </div>
                  </div>

                  <div className="card bg-base-100 shadow-lg">
                    <div className="card-body p-4 text-center sm:p-6">
                      <Mail className="mx-auto mb-3 h-6 w-6 text-info sm:mb-4 sm:h-8 sm:w-8" />
                      <h3 className="card-title justify-center text-base text-info sm:text-lg">
                        Email Support
                      </h3>
                      <p className="mb-3 text-sm text-base-content/70 sm:mb-4 sm:text-base">
                        safety@hareeng.com
                      </p>
                      <p className="text-xs text-base-content/60 sm:text-sm">
                        Non-urgent reports
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Always show email support */}
              <div className="mt-6 sm:mt-8">
                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body p-4 sm:p-6">
                    <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
                      <Mail className="h-6 w-6 text-info sm:h-8 sm:w-8" />
                      <div className="text-center sm:text-left">
                        <h3 className="text-base font-semibold text-info sm:text-lg">
                          Email Support
                        </h3>
                        <p className="text-sm text-base-content/70 sm:text-base">
                          For non-urgent reports:{" "}
                          <a
                            className="link link-primary"
                            href="mailto:hello@hareeng.org"
                          >
                            hello@hareeng.org
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
